/**
 * Lazy Loading Avatar Component
 * 
 * Wraps MUI Avatar with lazy loading support for images.
 * Uses Intersection Observer to load images only when they're about to enter the viewport.
 * 
 * Usage:
 *   <LazyAvatar
 *     src="/path/to/image.jpg"
 *     alt="Description"
 *     sx={{ width: 64, height: 64 }}
 *   >
 *     Fallback content
 *   </LazyAvatar>
 */

import React, { useState, useEffect, useRef } from 'react';
import { Avatar, AvatarProps } from '@mui/material';

export interface LazyAvatarProps extends Omit<AvatarProps, 'src'> {
  src?: string;
  alt?: string;
}

/**
 * Lazy Loading Avatar Component
 * Loads images only when they're about to enter the viewport
 */
const LazyAvatar: React.FC<LazyAvatarProps> = React.memo(({
  src,
  alt,
  children,
  ...avatarProps
}) => {
  const [isInView, setIsInView] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!avatarRef.current || !src) {
      // If no src, don't set up observer
      return;
    }

    // If browser doesn't support IntersectionObserver, load immediately
    if (typeof IntersectionObserver === 'undefined') {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before avatar enters viewport
        threshold: 0.01,
      }
    );

    const currentAvatar = avatarRef.current;
    if (currentAvatar) {
      observer.observe(currentAvatar);
    }

    return () => {
      if (currentAvatar) {
        observer.unobserve(currentAvatar);
      }
    };
  }, [src]);

  const handleImageLoad = () => {
    setIsLoaded(true);
  };

  const handleImageError = () => {
    setIsLoaded(false);
  };

  // If no src, render as normal Avatar
  if (!src) {
    return (
      <Avatar ref={avatarRef} {...avatarProps}>
        {children}
      </Avatar>
    );
  }

  return (
    <Avatar
      ref={avatarRef}
      {...avatarProps}
      src={isInView ? src : undefined}
      imgProps={{
        alt,
        onLoad: handleImageLoad,
        onError: handleImageError,
        loading: 'lazy',
        style: {
          opacity: isLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
        },
      }}
    >
      {!isLoaded && children}
    </Avatar>
  );
});

LazyAvatar.displayName = 'LazyAvatar';

export default LazyAvatar;

